import { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { Field, Input, Textarea, Btn, Toggle, Modal } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = { name: "", role: "", bio: "", photo: "", socials: { instagram: "", twitter: "", linkedin: "" }, active: true };

export default function AuthorsAdmin() {
  const [authors, setAuthors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/authors?active_only=false").then((r) => setAuthors(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a, socials: a.socials || {} }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    try {
      if (editing) await api.put(`/admin/authors/${editing.id}`, form);
      else await api.post("/admin/authors", form);
      toast.success("Autor guardado"); setOpen(false); load();
    } catch { toast.error("Error al guardar"); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar autor?")) return; await api.delete(`/admin/authors/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div>
      <header className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl tracking-tight">Autores</h1>
        <Btn variant="accent" onClick={openNew} data-testid="new-author-btn"><Plus size={15} /> Nuevo autor</Btn>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {authors.map((a) => (
          <div key={a.id} className="bg-white border border-sunset-border p-6" data-testid={`author-card-${a.id}`}>
            <div className="flex items-center gap-4 mb-4">
              {a.photo ? <img src={mediaUrl(a.photo)} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-sunset-border" />}
              <div>
                <p className="font-serif text-xl">{a.name}</p>
                <p className="text-xs text-sunset-muted uppercase tracking-wider">{a.role}</p>
              </div>
            </div>
            <p className="text-sm text-sunset-muted line-clamp-2 mb-4">{a.bio}</p>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${a.active ? "text-green-700" : "text-sunset-muted"}`}>{a.active ? "Activo" : "Inactivo"}</span>
              <div className="flex gap-2">
                <button onClick={() => openEdit(a)} className="p-2 hover:text-sunset-accent" data-testid={`edit-author-${a.id}`}><Edit size={16} /></button>
                <button onClick={() => del(a.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar autor" : "Nuevo autor"}>
        <div className="mb-4"><ImageUpload label="Foto" value={form.photo} onChange={(v) => set("photo", v)} /></div>
        <Field label="Nombre"><Input data-testid="author-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Cargo"><Input value={form.role} onChange={(e) => set("role", e.target.value)} /></Field>
        <Field label="Biografía"><Textarea rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} /></Field>
        <Field label="Instagram"><Input value={form.socials.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} /></Field>
        <Field label="Twitter / X"><Input value={form.socials.twitter || ""} onChange={(e) => setSocial("twitter", e.target.value)} /></Field>
        <div className="py-2 mb-4"><Toggle checked={form.active} onChange={(v) => set("active", v)} label="Activo" /></div>
        <Btn variant="accent" onClick={save} data-testid="save-author-btn">Guardar</Btn>
      </Modal>
    </div>
  );
}
